import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Activity, ArrowDown, ArrowUp, Backpack, Bed, Building2, Bus, Camera, ChevronDown,
  ChevronRight, Clock, Copy, FileCheck2, Flag, HeartPulse, Map, MapPin,
  MoreHorizontal, Package, Pencil, Plus, Save, ShieldAlert, ShieldCheck, Sparkles, Star,
  Trash2, UserRound, Utensils, X,
} from "lucide-react";
import { toast } from "sonner";
import { LoadingState, EmptyState, ErrorState } from "@/components/States";
import { Btn, Field, inputCls } from "@/components/Primitives";
import { SummitFormActions, SummitFormHeader } from "@/components/forms/SummitForm";
import ParticipantMediaImage from "@/components/participant/ParticipantMediaImage";
import { api, confirmAction, formatApiError, formatApiErrorDetail } from "@/lib/api";

const dayTemplate = {
  date: "", title: "", summary: "", description: "", meals: "",
  safety_notes: "", distance_km: "", elevation_gain_m: "", estimated_duration_min: "",
};
const activityTemplate = {
  name: "", title: "", activity_type: "", start_time: "", end_time: "",
  duration_min: "", location: "", description: "", difficulty: "", safety_notes: "",
};
const locationTemplate = {
  name: "", latitude: "", longitude: "", location_type: "place", role: "activity_site",
  arrival_time: "", departure_time: "", duration_min: "", visiting_notes: "",
};
const packTemplate = { name: "", category: "required", required: true, optional: false, description: "" };
const ACTIVITY_DRAFT_LOCATION_TARGET = "__activity_draft_location__";
const GEOAPIFY_API_KEY = (
  process.env.REACT_APP_GEOAPIFY_API_KEY ||
  process.env.REACT_APP_GEOAPIFY_KEY ||
  process.env.REACT_APP_OPENMAP_API_KEY ||
  process.env.REACT_APP_OPEN_MAP_API_KEY ||
  process.env.REACT_APP_OPENMAP_KEY ||
  process.env.REACT_APP_OPENMAPKEY ||
  ""
);

function normalizeGeoapifyGeocodeResult(item, fallback = "") {
  const props = item?.properties || item || {};
  const coordinates = item?.geometry?.coordinates || [];
  const lat = props.lat ?? coordinates[1];
  const lon = props.lon ?? coordinates[0];
  const formatted = props.formatted || [props.address_line1, props.address_line2].filter(Boolean).join(", ");
  const primary = props.name || props.address_line1 || props.city || props.village || props.town || props.county;
  return {
    place_id: props.place_id || `${lat},${lon}`,
    name: primary || formatted.split(",")[0] || fallback,
    display_name: formatted,
    lat,
    lon,
    source: "geoapify",
  };
}

function mapLocationSelection(result = {}, fallback = "") {
  const lat = result.lat ?? result.latitude;
  const lon = result.lon ?? result.longitude;
  const label = result.name || result.display_name?.split(",")[0] || result.visiting_notes?.split(",")[0] || fallback;
  return {
    name: label,
    display_name: result.display_name || result.visiting_notes || label,
    lat,
    lon,
  };
}

const TILE_SIZE = 256;
const MIN_MAP_ZOOM = 2;
const MAX_MAP_ZOOM = 18;

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function lonLatToWorld(lonValue, latValue, zoom) {
  const lon = clampNumber(Number(lonValue), -180, 180);
  const lat = clampNumber(Number(latValue), -85.05112878, 85.05112878);
  const scale = 2 ** zoom;
  const latRad = (lat * Math.PI) / 180;
  return {
    x: ((lon + 180) / 360) * scale * TILE_SIZE,
    y: ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * scale * TILE_SIZE,
  };
}

function worldToLonLat(x, y, zoom) {
  const scale = 2 ** zoom;
  const lon = (x / (TILE_SIZE * scale)) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * y) / (TILE_SIZE * scale);
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return {
    lat: clampNumber(lat, -85.05112878, 85.05112878),
    lon: clampNumber(lon, -180, 180),
  };
}

function geoapifyTileGrid(latValue, lonValue, apiKey, zoom = 14) {
  const lat = Number(latValue);
  const lon = Number(lonValue);
  if (!apiKey || !Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  const world = lonLatToWorld(lon, lat, zoom);
  const exactX = world.x / TILE_SIZE;
  const exactY = world.y / TILE_SIZE;
  const x = Math.floor(exactX);
  const y = Math.floor(exactY);
  const tiles = [];
  const radius = 2;
  const mapTileCount = 2 ** zoom;
  for (let dy = -radius; dy <= radius; dy += 1) {
    for (let dx = -radius; dx <= radius; dx += 1) {
      const tileX = ((x + dx) % mapTileCount + mapTileCount) % mapTileCount;
      const tileY = clampNumber(y + dy, 0, mapTileCount - 1);
      tiles.push({
        key: `${zoom}-${tileX}-${tileY}-${dx}-${dy}`,
        src: `https://maps.geoapify.com/v1/tile/osm-bright/${zoom}/${tileX}/${tileY}.png?apiKey=${encodeURIComponent(apiKey)}`,
      });
    }
  }
  return {
    tiles,
    lat,
    lon,
    zoom,
    tileCount: radius * 2 + 1,
    centerX: (radius + (exactX - x)) * TILE_SIZE,
    centerY: (radius + (exactY - y)) * TILE_SIZE,
  };
}

function toFormDay(day = {}) {
  return {
    date: day.date || "",
    title: day.title || "",
    summary: day.summary || "",
    description: day.description || "",
    meals: (day.meals || []).join(", "),
    safety_notes: day.safety_notes || "",
    distance_km: day.distance_km ?? "",
    elevation_gain_m: day.elevation_gain_m ?? "",
    estimated_duration_min: day.estimated_duration_min ?? "",
  };
}

function dayPayload(form) {
  return {
    title: form.title.trim(),
    date: form.date || null,
    summary: form.summary,
    description: form.description,
    meals: form.meals.split(",").map((m) => m.trim()).filter(Boolean),
    safety_notes: form.safety_notes,
    distance_km: form.distance_km === "" ? null : Number(form.distance_km),
    elevation_gain_m: form.elevation_gain_m === "" ? null : Number(form.elevation_gain_m),
    estimated_duration_min: form.estimated_duration_min === "" ? null : Number(form.estimated_duration_min),
  };
}

function normalizeClock(value) {
  if (!value) return null;
  const match = String(value).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return value;
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

function activityTitleValue(form = {}) {
  return [form.title, form.name]
    .map((value) => String(value || "").trim())
    .find(Boolean) || "";
}

function activityPayload(form) {
  const title = activityTitleValue(form);
  const durationValue = form.duration_min;
  const payload = {
    name: title,
    title,
    activity_type: form.activity_type || "",
    start_time: normalizeClock(form.start_time),
    end_time: normalizeClock(form.end_time),
    duration_min: durationValue === "" || durationValue == null ? null : Number(durationValue),
    location: form.location || null,
    description: form.description,
    difficulty: form.difficulty || null,
    safety_notes: form.safety_notes,
  };
  if (form.display_order != null) payload.display_order = form.display_order;
  return payload;
}

function dateInputValue(value) {
  return value ? String(value).slice(0, 10) : "";
}

function isOutsideDateRange(date, start, end) {
  return Boolean(date && start && end && (date < start || date > end));
}

function resizeTextareaToContent(textarea) {
  if (!textarea) return;
  textarea.style.height = "auto";
  textarea.style.height = `${textarea.scrollHeight}px`;
}

function AutoGrowTextarea({ className = "", value, onChange, rows = 1, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    resizeTextareaToContent(ref.current);
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={`${inputCls} resize-none overflow-hidden ${className}`}
      value={value}
      onChange={(e) => {
        onChange?.(e);
        resizeTextareaToContent(e.target);
      }}
      {...props}
    />
  );
}

function keepSingleLine(e) {
  if (e.key === "Enter") e.preventDefault();
}

function dayPackingItems(day = {}) {
  return Array.from(new globalThis.Map([
    ...(day.packing_requirements || []),
    ...(day.activities || []).flatMap((activity) => activity.packing_requirements || []),
  ].map((item, index) => [item.id || `${item.name || "packing"}-${index}`, item])).values());
}

function editableDayPackingItem(day = {}) {
  return (day.packing_requirements || []).find((item) => !item.activity_id) || null;
}

function packingItemNames(value) {
  const seen = new Set();
  return String(value || "")
    .split(/[,;\n]+/)
    .map((item) => item.trim().replace(/\s+/g, " "))
    .filter((item) => {
      const key = item.toLocaleLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function vendorListText(value) {
  return Array.isArray(value) ? value.filter(Boolean).join(", ") : value || "";
}

function vendorRatingValue(value) {
  const rating = Number(value);
  return Number.isFinite(rating) && rating > 0 ? Math.min(5, Math.max(0, rating)) : null;
}

const vendorCategoryMeta = {
  accommodation: { icon: Bed, badge: "bg-[#E7F2ED] text-[#2D7A65] ring-[#BFD5C6]", pulse: "bg-[#2D7A65]" },
  transport: { icon: Bus, badge: "bg-[#EAF2FF] text-[#315D8A] ring-[#BED1EA]", pulse: "bg-[#315D8A]" },
  "equipment rental": { icon: Package, badge: "bg-[#F7F1E5] text-[#7A5D2D] ring-[#E6D7B9]", pulse: "bg-[#C8962D]" },
  catering: { icon: Utensils, badge: "bg-[#FFF1E7] text-[#A0522D] ring-[#F0C9B1]", pulse: "bg-[#D96B35]" },
  "local guide": { icon: UserRound, badge: "bg-[#E8F5EE] text-[#236D5C] ring-[#B7DEC9]", pulse: "bg-[#2D7A65]" },
  "permit provider": { icon: FileCheck2, badge: "bg-[#F0EDFF] text-[#5C4D9A] ring-[#D7CFF5]", pulse: "bg-[#6C5BAA]" },
  "medical support": { icon: HeartPulse, badge: "bg-[#FEEAEC] text-[#A33B4B] ring-[#F3C2CA]", pulse: "bg-[#D95366]" },
  photographer: { icon: Camera, badge: "bg-[#E7F7F8] text-[#24717B] ring-[#BEE1E4]", pulse: "bg-[#2A8C99]" },
  "activity provider": { icon: Activity, badge: "bg-[#EDF6E8] text-[#4F752B] ring-[#CBE0BC]", pulse: "bg-[#6E9E3B]" },
  porter: { icon: Backpack, badge: "bg-[#F2EFE8] text-[#65513B] ring-[#DBD0BE]", pulse: "bg-[#8A6A48]" },
  insurance: { icon: ShieldCheck, badge: "bg-[#E9F0FF] text-[#415E9B] ring-[#C6D5F5]", pulse: "bg-[#526EB0]" },
  other: { icon: Building2, badge: "bg-[#EEF1EF] text-[#516259] ring-[#D5DDD7]", pulse: "bg-[#516259]" },
};

function VendorCategoryBadge({ category }) {
  const meta = vendorCategoryMeta[String(category || "").toLowerCase()] || vendorCategoryMeta.other;
  const Icon = meta.icon;
  return (
    <span className={`relative inline-flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-2xl ring-1 ${meta.badge}`}>
      <span className={`absolute inset-1 rounded-2xl opacity-0 motion-safe:group-hover:animate-ping motion-safe:group-hover:opacity-25 ${meta.pulse}`} />
      <Icon className="relative h-4 w-4" />
    </span>
  );
}

function OperationalCard({ kind, item, onClick }) {
  const title = item.title || (kind === "transport" ? item.provider : item.property_name);
  const timing = kind === "transport"
    ? [item.departure_at, item.arrival_at].filter(Boolean).join(" to ")
    : [item.check_in, item.check_out].filter(Boolean).join(" to ");
  const location = item.address || item.region || "";
  const services = vendorListText(item.service_types);
  const rating = vendorRatingValue(item.rating);
  const hasCharges = item.charges !== null && item.charges !== undefined && item.charges !== "";
  const hasVendorSummary = location || services || rating || hasCharges;
  return (
    <button
      type="button"
      onClick={onClick}
      className="group w-full rounded-md border border-[#E2DFD7] bg-white p-3 text-left text-sm transition hover:-translate-y-0.5 hover:border-[#2D7A65] hover:bg-[#FFFCF7] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]"
      data-testid={`${kind}-itinerary-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <VendorCategoryBadge category={item.category || kind} />
          <div className="min-w-0">
            <span className="block truncate font-semibold">{title}</span>
            <span className="text-xs capitalize text-slate-500">{item.category || kind}</span>
          </div>
        </div>
        <span className="shrink-0 text-xs capitalize text-slate-500">{item.status || "planned"} </span>
      </div>
      {timing && <p className="mt-1 text-slate-600">{timing}</p>}
      {item.provider && item.provider !== title && <p className="mt-1 text-xs text-slate-500">{item.provider}</p>}
      {hasVendorSummary && (
        <div className="mt-2 rounded-md border border-[#E2DFD7] bg-[#FFFCF7] p-2 text-xs text-slate-600">
          {location && <p>{location}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            {rating && (
              <span className="inline-flex items-center gap-1">
                <span className="inline-flex text-[#E5B85C]" aria-label={`Rating ${rating} out of 5`}>
                  {[0, 1, 2, 3, 4].map((index) => (
                    <Star key={index} className={`h-3.5 w-3.5 ${index < Math.round(rating) ? "fill-current" : ""}`} />
                  ))}
                </span>
                <span>{rating}/5</span>
              </span>
            )}
          </div>
          {services && <p className="mt-1">Services: {services}</p>}
        </div>
      )}
    </button>
  );
}

function vendorAssignmentLogisticsItem(assignment, kind) {
  const vendor = assignment.vendor || {};
  return {
    id: `vendor-${assignment.id}`,
    title: vendor.name || assignment.assignment_type || "Organization vendor",
    category: vendor.category || kind,
    type: assignment.assignment_type || vendor.category || kind,
    property_name: vendor.name || assignment.assignment_type || "Accommodation",
    provider: vendor.name || "Organization vendor",
    status: assignment.status || "planned",
    notes: assignment.trip_specific_notes || vendor.details || "",
    region: vendor.region || "",
    address: vendor.address || "",
    rating: vendor.rating ?? "",
    charges: vendor.charges ?? "",
    service_types: vendor.service_types || [],
    departure_at: assignment.start_date || "",
    arrival_at: assignment.end_date || "",
    check_in: assignment.start_date || "",
    check_out: assignment.end_date || "",
  };
}

function modalShell({ title, subtitle, onClose, children, testId, label }) {
  return createPortal(
    <div className="summit-form-overlay z-[1000]" role="dialog" aria-modal="true" aria-label={label || title} data-testid={testId}>
      <div className="summit-form summit-form--sheet max-w-5xl">
        <SummitFormHeader eyebrow="Itinerary builder" title={title} description={subtitle} onClose={onClose} />
        {children}
      </div>
    </div>,
    document.body,
  );
}

function uniqueText(items = []) {
  const seen = new Set();
  return (items || []).map((item) => String(item || "").trim()).filter(Boolean).filter((item) => {
    const key = item.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function joinText(items = [], fallback = "") {
  const text = uniqueText(items).join(" - ");
  return text || fallback;
}

export default function ItineraryTab({ trip, canEdit, onGoTab }) {
  const [days, setDays] = useState(null);
  const [experience, setExperience] = useState(null);
  const [experienceDraft, setExperienceDraft] = useState(null);
  const [editingExperience, setEditingExperience] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [editingDay, setEditingDay] = useState(null);
  const [dayForm, setDayForm] = useState(dayTemplate);
  const [activityFor, setActivityFor] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [activityForm, setActivityForm] = useState(activityTemplate);
  const [locationFor, setLocationFor] = useState(null);
  const [locationForm, setLocationForm] = useState(locationTemplate);
  const [locationQuery, setLocationQuery] = useState("");
  const [locationResults, setLocationResults] = useState([]);
  const [locationSearching, setLocationSearching] = useState(false);
  const [locationSearchError, setLocationSearchError] = useState("");
  const [locationMapZoom, setLocationMapZoom] = useState(14);
  const [mediaFor, setMediaFor] = useState(null);
  const [pendingActivityFiles, setPendingActivityFiles] = useState([]);
  const [pendingActivityLocation, setPendingActivityLocation] = useState(null);
  const [photoPanelHeights, setPhotoPanelHeights] = useState({});
  const [actionMenu, setActionMenu] = useState(null);
  const [packingForm, setPackingForm] = useState(packTemplate);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingDays, setGeneratingDays] = useState(false);
  const [error, setError] = useState("");
  const mediaFileInputRef = useRef(null);
  const mapDragRef = useRef(null);
  const dayContentObserversRef = useRef(new globalThis.Map());
  const tripStartDate = dateInputValue(trip.start_date);
  const tripEndDate = dateInputValue(trip.end_date);
  const hasTripDateRange = Boolean(tripStartDate && tripEndDate);

  const load = useCallback(async () => {
    setError("");
    try {
      const { data: dayData } = await api.get(`/trips/${trip.id}/days`);
      setDays(dayData.data);
    } catch (err) {
      setError(formatApiError(err, "Could not load itinerary."));
    }
  }, [trip.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!dirty) return undefined;
    const beforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);
  useEffect(() => {
    if (!mediaFor?.pickFile) return undefined;
    const timer = window.setTimeout(() => mediaFileInputRef.current?.click(), 0);
    setMediaFor((prev) => (prev?.pickFile ? { ...prev, pickFile: false } : prev));
    return () => window.clearTimeout(timer);
  }, [mediaFor]);
  useEffect(() => {
    if (!actionMenu) return undefined;
    const closeMenu = () => setActionMenu(null);
    const closeOnEscape = (e) => { if (e.key === "Escape") closeMenu(); };
    const closeOnPointerDown = (e) => {
      if (!e.target.closest("[data-itinerary-action-menu]")) closeMenu();
    };
    window.addEventListener("resize", closeMenu);
    window.addEventListener("scroll", closeMenu, true);
    window.addEventListener("keydown", closeOnEscape);
    window.addEventListener("pointerdown", closeOnPointerDown);
    return () => {
      window.removeEventListener("resize", closeMenu);
      window.removeEventListener("scroll", closeMenu, true);
      window.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("pointerdown", closeOnPointerDown);
    };
  }, [actionMenu]);
  useEffect(() => () => {
    dayContentObserversRef.current.forEach(({ observer }) => observer?.disconnect());
    dayContentObserversRef.current.clear();
  }, []);

  const setDayContentMeasureRef = useCallback((dayId) => (node) => {
    const existing = dayContentObserversRef.current.get(dayId);
    existing?.observer?.disconnect();
    if (!node || typeof ResizeObserver === "undefined") {
      dayContentObserversRef.current.delete(dayId);
      return;
    }
    const updateHeight = () => {
      const nextHeight = Math.ceil(node.getBoundingClientRect().height);
      setPhotoPanelHeights((prev) => (prev[dayId] === nextHeight ? prev : { ...prev, [dayId]: nextHeight }));
    };
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    dayContentObserversRef.current.set(dayId, { node, observer });
    updateHeight();
  }, []);

  const missingDayCount = useMemo(() => {
    const duration = Number(trip.duration_days) || 0;
    if (!days || duration < 1) return 0;
    const planned = new Set(days.map((day) => Number(day.day_number)).filter((n) => Number.isInteger(n)));
    let missing = 0;
    for (let dayNumber = 1; dayNumber <= duration; dayNumber += 1) {
      if (!planned.has(dayNumber)) missing += 1;
    }
    return missing;
  }, [days, trip.duration_days]);

  const safeReload = async () => {
    if (dirty && !confirmAction("Discard unsaved itinerary edits?")) return;
    setDirty(false);
    await load();
  };

  const saveDay = async (e) => {
    e.preventDefault();
    if (!dayForm.title.trim()) return toast.error("Day title is required");
    if (dayForm.date && !hasTripDateRange) return toast.error("Set the trip start and end dates before choosing an itinerary day date");
    if (isOutsideDateRange(dayForm.date, tripStartDate, tripEndDate)) return toast.error("Choose a date within the trip start and end dates");
    setSaving(true);
    try {
      let savedDayId = editingDay?.id;
      if (savedDayId) await api.put(`/days/${savedDayId}`, dayPayload(dayForm));
      else {
        const created = await api.post(`/trips/${trip.id}/days`, dayPayload(dayForm));
        savedDayId = created.data.id;
      }
      const packingName = packingForm.name.trim();
      if (packingName && savedDayId) {
        const { id: packingItemId, ...packingValues } = packingForm;
        const payload = { ...packTemplate, ...packingValues, name: packingName, itinerary_day_id: savedDayId, activity_id: null };
        if (packingItemId) await api.put(`/packing-items/${packingItemId}`, payload);
        else await api.post(`/trips/${trip.id}/packing-items`, payload);

        // The itinerary keeps its day context, while Packing is a separate,
        // trip-wide checklist. Only automatically-created entries are refreshed
        // here, so an organizer's manual Packing-tab edits are never overwritten.
        const { data: packingData } = await api.get(`/trips/${trip.id}/packing-items`);
        const tripWideByName = new globalThis.Map(
          (packingData.data || [])
            .filter((item) => !item.itinerary_day_id && !item.activity_id && !item.participant_id)
            .map((item) => [String(item.name || "").trim().toLocaleLowerCase(), item]),
        );
        const description = packingForm.description.trim();
        await Promise.all(packingItemNames(packingName).map(async (name) => {
          const existing = tripWideByName.get(name.toLocaleLowerCase());
          if (!existing) {
            await api.post(`/trips/${trip.id}/packing-items`, {
              ...packTemplate,
              name,
              description,
              itinerary_day_id: null,
              activity_id: null,
              source: "template",
            });
          } else if (existing.source === "template" && existing.description !== description) {
            await api.put(`/packing-items/${existing.id}`, {
              name,
              description,
              itinerary_day_id: null,
              activity_id: null,
            });
          }
        }));
      }
      toast.success(editingDay?.id ? "Day saved" : "Day added");
      setEditingDay(null); setDayForm(dayTemplate); setPackingForm(packTemplate); setDirty(false); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const editDay = (day) => {
    const packingItem = editableDayPackingItem(day);
    setEditingDay(day);
    setDayForm(toFormDay(day));
    setPackingForm(packingItem ? { ...packTemplate, ...packingItem, itinerary_day_id: day.id } : { ...packTemplate, itinerary_day_id: day.id });
    setDirty(false);
  };
  const newDay = () => { setEditingDay({}); setDayForm(dayTemplate); setPackingForm(packTemplate); setDirty(false); };
  const duplicateDay = async (day) => {
    try {
      const created = await api.post(`/trips/${trip.id}/days`, { ...dayPayload(toFormDay(day)), title: `${day.title} Copy` });
      for (const a of day.activities || []) await api.post(`/days/${created.data.id}/activities`, activityPayload(a));
      toast.success("Day duplicated"); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const moveDay = async (day, direction) => {
    const idx = days.findIndex((d) => d.id === day.id);
    const other = days[idx + direction];
    if (!other) return;
    const reordered = [...days];
    [reordered[idx], reordered[idx + direction]] = [reordered[idx + direction], reordered[idx]];
    try {
      await api.put(`/trips/${trip.id}/days/reorder`, { ordered_ids: reordered.map((d) => d.id) });
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const deleteDay = async (id) => {
    if (!confirmAction("Remove this itinerary day and its activities?")) return;
    try { await api.delete(`/days/${id}`); toast.success("Day removed"); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openActivity = (dayId, activity = null) => {
    const targetDay = (days || []).find((day) => day.id === dayId);
    const nextDisplayOrder = (targetDay?.activities || []).length + 1;
    setActionMenu(null);
    setLocationFor(null);
    setMediaFor(null);
    setPendingActivityFiles([]);
    setActivityFor(dayId);
    setEditingActivity(activity);
    setActivityForm(activity
      ? { ...activityTemplate, ...activity, name: activity.title || activity.name || "" }
      : { ...activityTemplate, display_order: nextDisplayOrder });
    if (dayId) setExpanded((prev) => ({ ...prev, [dayId]: true }));
    setDirty(false);
  };
  const saveActivity = async (e) => {
    e.preventDefault();
    if (!activityTitleValue(activityForm)) return toast.error("Activity title is required");
    if (!editingActivity?.id && !activityFor) return toast.error("Choose a day before adding an activity");
    setSaving(true);
    try {
      let savedActivityId = editingActivity?.id;
      if (savedActivityId) await api.put(`/activities/${savedActivityId}`, activityPayload(activityForm));
      else {
        const targetDay = (days || []).find((day) => day.id === activityFor);
        const orderedIds = (targetDay?.activities || []).map((activity) => activity.id).filter(Boolean);
        const needsOrderRepair = (targetDay?.activities || []).some((activity, index) => Number(activity.display_order) !== index + 1);
        if (orderedIds.length > 0 && needsOrderRepair) {
          await api.put(`/days/${activityFor}/activities/reorder`, { ordered_ids: orderedIds });
        }
        const { data: createdActivity } = await api.post(`/days/${activityFor}/activities`, {
          ...activityPayload(activityForm),
          display_order: orderedIds.length + 1,
        });
        savedActivityId = createdActivity.id;
      }
      if (pendingActivityLocation && savedActivityId) {
        await attachLocationToActivity(savedActivityId, pendingActivityLocation, editingActivity || null);
      }
      let photosUploaded = true;
      if (pendingActivityFiles.length > 0 && savedActivityId) {
        photosUploaded = await uploadMediaFiles({ type: "activity", id: savedActivityId }, pendingActivityFiles, { showToast: false });
      }
      toast.success(`${editingActivity?.id ? "Activity saved" : "Activity added"}${pendingActivityFiles.length > 0 && photosUploaded ? " with photos" : ""}`);
      setExpanded((prev) => (activityFor ? { ...prev, [activityFor]: true } : prev));
      setActivityFor(null); setEditingActivity(null); setActivityForm({ ...activityTemplate }); setPendingActivityFiles([]); setPendingActivityLocation(null); setDirty(false); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const duplicateActivity = async (dayId, activity) => {
    try {
      await api.post(`/days/${dayId}/activities`, { ...activityPayload(activity), name: `${activity.title || activity.name} Copy` });
      toast.success("Activity duplicated"); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const moveActivity = async (day, activity, direction) => {
    const list = day.activities || [];
    const idx = list.findIndex((a) => a.id === activity.id);
    const other = list[idx + direction];
    if (!other) return;
    const reordered = [...list];
    [reordered[idx], reordered[idx + direction]] = [reordered[idx + direction], reordered[idx]];
    try {
      await api.put(`/days/${day.id}/activities/reorder`, { ordered_ids: reordered.map((item) => item.id) });
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const deleteActivity = async (id) => {
    if (!confirmAction("Remove this activity?")) return;
    try { await api.delete(`/activities/${id}`); toast.success("Activity removed"); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const appendMediaToTarget = (target, mediaItems = []) => {
    if (!target || mediaItems.length === 0) return;
    setDays((prev) => (prev || []).map((day) => {
      if (target.type === "day" && day.id === target.id) {
        return { ...day, photos: [...(day.photos || []).filter((photo) => photo.id !== target.replaceId), ...mediaItems] };
      }
      return {
        ...day,
        activities: (day.activities || []).map((activity) => (
          target.type === "activity" && activity.id === target.id
            ? { ...activity, photos: [...(activity.photos || []).filter((photo) => photo.id !== target.replaceId), ...mediaItems] }
            : activity
        )),
      };
    }));
  };

  const removeMediaFromDays = (mediaId) => {
    setDays((prev) => (prev || []).map((day) => ({
      ...day,
      photos: (day.photos || []).filter((photo) => photo.id !== mediaId),
      activities: (day.activities || []).map((activity) => ({
        ...activity,
        photos: (activity.photos || []).filter((photo) => photo.id !== mediaId),
      })),
    })));
  };

  const uploadMediaFiles = async (target, files = [], options = {}) => {
    const selectedFiles = [...files].filter(Boolean);
    if (!target || selectedFiles.length === 0) return false;
    const { showToast = true } = options;
    setSaving(true);
    try {
      const filesToUpload = target.replaceId ? selectedFiles.slice(0, 1) : selectedFiles;
      const uploaded = [];
      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("relation_type", target.type || "trip");
        if (target.type === "day") formData.append("day_id", target.id);
        if (target.type === "activity") formData.append("activity_id", target.id);
        formData.append("is_cover", "false");
        const { data } = await api.post(`/trips/${trip.id}/media/upload`, formData);
        uploaded.push(data);
      }
      if (target.replaceId) await api.delete(`/media/${target.replaceId}`);
      appendMediaToTarget(target, uploaded);
      if (showToast) toast.success(target.replaceId ? "Photo replaced" : `${filesToUpload.length} photo${filesToUpload.length === 1 ? "" : "s"} added`);
      setMediaFor(null); setDirty(false);
      return true;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); return false; }
    finally { setSaving(false); }
  };
  const deleteMedia = async (media) => {
    if (!confirmAction("Remove this photo?")) return;
    try {
      await api.delete(`/media/${media.id}`);
      removeMediaFromDays(media.id);
      toast.success("Photo removed");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const searchLocations = async (queryText = locationQuery) => {
    const q = queryText.trim();
    if (!q) {
      setLocationSearchError("Search for a place first.");
      return;
    }
    const context = [trip.destination, trip.region].filter(Boolean).join(", ");
    const scopedQuery = context && !q.toLowerCase().includes(context.toLowerCase()) ? `${q}, ${context}` : q;
    setLocationSearching(true);
    setLocationSearchError("");
    try {
      let data = [];
      try {
        const { data: resp } = await api.get("/maps/geocode", { params: { q: scopedQuery, trip_id: trip.id } });
        data = Array.isArray(resp?.data) ? resp.data : [];
      } catch (err) {
        if (!GEOAPIFY_API_KEY) throw err;
        const geoapifyResp = await fetch(`https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(scopedQuery)}&limit=6&format=json&apiKey=${encodeURIComponent(GEOAPIFY_API_KEY)}`, {
          headers: { Accept: "application/json" },
        });
        if (!geoapifyResp.ok) throw err;
        const geoapifyData = await geoapifyResp.json();
        data = (geoapifyData.results || []).slice(0, 6).map((item) => normalizeGeoapifyGeocodeResult(item, q)).filter((item) => item.lat != null && item.lon != null);
      }
      setLocationResults(data);
      if (data?.length) {
        const first = data[0];
        setLocationForm((prev) => ({
          ...prev,
          name: first.name || first.display_name?.split(",")[0] || q,
          latitude: Number(first.lat).toFixed(6),
          longitude: Number(first.lon).toFixed(6),
          visiting_notes: first.display_name || prev.visiting_notes,
        }));
      } else {
        setLocationSearchError("No matching places found.");
      }
    } catch (err) {
      setLocationSearchError(formatApiError(err, "Could not search places. Check Geoapify configuration, then try again."));
    } finally {
      setLocationSearching(false);
    }
  };
  const attachLocationToActivity = async (activityId, result, currentActivity = null) => {
    const selection = mapLocationSelection(result, locationQuery);
    const label = selection.name;
    if (!activityId || !label) return false;
    const loc = await api.post("/locations", {
      name: label,
      latitude: selection.lat == null ? null : Number(selection.lat),
      longitude: selection.lon == null ? null : Number(selection.lon),
      location_type: locationTemplate.location_type,
      visiting_notes: selection.display_name || "",
    });
    await api.post(`/activities/${activityId}/locations`, {
      location_id: loc.data.id,
      role: locationTemplate.role,
      sequence: 1,
      arrival_time: null,
      departure_time: null,
      duration_min: null,
    });
    for (const rel of currentActivity?.activity_locations || []) {
      if (rel.location_id !== loc.data.id) await api.delete(`/activities/${activityId}/locations/${rel.id}`);
    }
    if (currentActivity) await api.put(`/activities/${activityId}`, { ...activityPayload(currentActivity), location: label });
    return true;
  };
  const attachLocationResult = async (result = locationForm) => {
    const selection = mapLocationSelection(result, locationQuery);
    const label = selection.name;
    if (!label) return toast.error("Choose a location first");
    if (locationFor === ACTIVITY_DRAFT_LOCATION_TARGET) {
      setPendingActivityLocation(selection);
      setActivityForm((prev) => ({ ...prev, location: label }));
      toast.success("Location selected");
      setLocationFor(null);
      setLocationResults([]);
      setLocationSearchError("");
      setDirty(true);
      return;
    }
    setSaving(true);
    try {
      const currentActivity = (days || []).flatMap((day) => day.activities || []).find((activity) => activity.id === locationFor);
      await attachLocationToActivity(locationFor, selection, currentActivity);
      if (editingActivity?.id === locationFor) setActivityForm((prev) => ({ ...prev, location: label }));
      toast.success("Location set");
      setLocationFor(null);
      setLocationResults([]);
      setLocationSearchError("");
      if (editingActivity?.id === locationFor) setDirty(true);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const previewLocationResult = (result) => {
    const selection = mapLocationSelection(result, locationQuery);
    setLocationForm((prev) => ({
      ...prev,
      name: selection.name || prev.name,
      latitude: selection.lat == null ? "" : Number(selection.lat).toFixed(6),
      longitude: selection.lon == null ? "" : Number(selection.lon).toFixed(6),
      visiting_notes: selection.display_name || prev.visiting_notes,
    }));
  };
  const reversePickedLocation = async (lat, lon) => {
    if (!GEOAPIFY_API_KEY) return;
    try {
      const resp = await fetch(`https://api.geoapify.com/v1/geocode/reverse?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&format=json&apiKey=${encodeURIComponent(GEOAPIFY_API_KEY)}`, {
        headers: { Accept: "application/json" },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const first = normalizeGeoapifyGeocodeResult(data.results?.[0], "");
      if (!first?.name) return;
      setLocationForm((prev) => {
        if (Math.abs(Number(prev.latitude) - Number(lat)) > 0.0001 || Math.abs(Number(prev.longitude) - Number(lon)) > 0.0001) return prev;
        return {
          ...prev,
          name: first.name,
          visiting_notes: first.display_name || prev.visiting_notes,
        };
      });
      setLocationQuery(first.name);
    } catch (err) {
      // Manual map picking still works if reverse geocoding is unavailable.
    }
  };
  const updateMapPickedLocation = (lat, lon, label = "", options = {}) => {
    const safeLat = clampNumber(Number(lat), -85.05112878, 85.05112878);
    const safeLon = clampNumber(Number(lon), -180, 180);
    if (!Number.isFinite(safeLat) || !Number.isFinite(safeLon)) return;
    const coordinateLabel = `${safeLat.toFixed(5)}, ${safeLon.toFixed(5)}`;
    setLocationForm((prev) => ({
      ...prev,
      name: label || `Map pin ${coordinateLabel}`,
      latitude: safeLat.toFixed(6),
      longitude: safeLon.toFixed(6),
      visiting_notes: label || `Picked on map at ${coordinateLabel}`,
    }));
    setDirty(true);
    if (options.reverse) reversePickedLocation(safeLat, safeLon);
  };
  const mapPointFromPointerEvent = (e) => {
    const lat = Number(locationForm.latitude);
    const lon = Number(locationForm.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    const rect = e.currentTarget.getBoundingClientRect();
    const center = lonLatToWorld(lon, lat, locationMapZoom);
    const x = center.x + e.clientX - (rect.left + rect.width / 2);
    const y = center.y + e.clientY - (rect.top + rect.height / 2);
    return worldToLonLat(x, y, locationMapZoom);
  };
  const handleMapPointerDown = (e) => {
    const lat = Number(locationForm.latitude);
    const lon = Number(locationForm.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    mapDragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      center: lonLatToWorld(lon, lat, locationMapZoom),
      dragged: false,
    };
  };
  const handleMapPointerMove = (e) => {
    const drag = mapDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (Math.abs(dx) + Math.abs(dy) > 4) drag.dragged = true;
    const next = worldToLonLat(drag.center.x - dx, drag.center.y - dy, locationMapZoom);
    updateMapPickedLocation(next.lat, next.lon);
  };
  const handleMapPointerUp = (e) => {
    const drag = mapDragRef.current;
    mapDragRef.current = null;
    if (drag?.pointerId != null && e.currentTarget.hasPointerCapture?.(drag.pointerId)) {
      e.currentTarget.releasePointerCapture(drag.pointerId);
    }
    if (drag?.dragged) {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const next = worldToLonLat(drag.center.x - dx, drag.center.y - dy, locationMapZoom);
      updateMapPickedLocation(next.lat, next.lon, "", { reverse: true });
      return;
    }
    const picked = mapPointFromPointerEvent(e);
    if (picked) updateMapPickedLocation(picked.lat, picked.lon, "", { reverse: true });
  };
  const zoomLocationMap = (delta) => {
    setLocationMapZoom((prev) => clampNumber(prev + delta, MIN_MAP_ZOOM, MAX_MAP_ZOOM));
  };
  const openLocation = (activity) => {
    if (locationFor === activity.id) {
      setLocationFor(null);
      return;
    }
    const initialQuery = activity.location || activity.title || activity.name || trip.destination || trip.region || "";
    const existingLocation = (activity.activity_locations || [])[0]?.location;
    setLocationFor(activity.id);
    setLocationForm(existingLocation ? {
      ...locationTemplate,
      name: existingLocation.name || initialQuery,
      latitude: existingLocation.latitude ?? "",
      longitude: existingLocation.longitude ?? "",
      visiting_notes: existingLocation.visiting_notes || "",
    } : { ...locationTemplate, name: initialQuery });
    setLocationQuery(initialQuery);
    setLocationResults([]);
    setLocationSearchError("");
    setLocationMapZoom(14);
    setMediaFor(null);
    if (initialQuery) window.setTimeout(() => searchLocations(initialQuery), 0);
  };
  const openDraftActivityLocation = () => {
    if (locationFor === ACTIVITY_DRAFT_LOCATION_TARGET) {
      setLocationFor(null);
      return;
    }
    const initialQuery = activityForm.location || activityForm.title || activityForm.name || trip.destination || trip.region || "";
    setLocationFor(ACTIVITY_DRAFT_LOCATION_TARGET);
    setLocationForm(pendingActivityLocation ? {
      ...locationTemplate,
      name: pendingActivityLocation.name || initialQuery,
      latitude: pendingActivityLocation.lat == null ? "" : Number(pendingActivityLocation.lat).toFixed(6),
      longitude: pendingActivityLocation.lon == null ? "" : Number(pendingActivityLocation.lon).toFixed(6),
      visiting_notes: pendingActivityLocation.display_name || "",
    } : { ...locationTemplate, name: initialQuery });
    setLocationQuery(initialQuery);
    setLocationResults([]);
    setLocationSearchError("");
    setLocationMapZoom(14);
    setMediaFor(null);
    if (initialQuery) window.setTimeout(() => searchLocations(initialQuery), 0);
  };
  const openMedia = (target, media = null) => {
    setMediaFor({ ...target, replaceId: media?.id || null, pickFile: true });
    setLocationFor(null);
    setDirty(false);
  };
  const handlePhotoFileSelect = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    e.target.value = "";
    await uploadMediaFiles(mediaFor, files);
  };
  const renderActivityLocationPicker = (savedActivity) => {
    const activeLocationTarget = savedActivity?.id || ACTIVITY_DRAFT_LOCATION_TARGET;
    if (!canEdit || locationFor !== activeLocationTarget) return null;
    const tileMap = geoapifyTileGrid(locationForm.latitude, locationForm.longitude, GEOAPIFY_API_KEY);
    const hasSelectedLocation = Boolean(locationForm.name && locationForm.latitude && locationForm.longitude);
    return (
      <div className="mt-3 grid gap-3 rounded-lg border border-[#E2DFD7] bg-white p-3 lg:grid-cols-[minmax(16rem,0.45fr)_minmax(0,1fr)]" data-testid="activity-location-picker">
        <div className="space-y-3">
          <div className="flex gap-2">
            <input className={inputCls} value={locationQuery} onChange={(e) => setLocationQuery(e.target.value)} placeholder="Search place or address" aria-label="Search map location" />
            <Btn type="button" variant="secondary" disabled={locationSearching} onClick={() => searchLocations()}><MapPin className="h-4 w-4" /> {locationSearching ? "Searching" : "Search"}</Btn>
          </div>
          {locationSearchError && <p className="text-xs text-amber-700">{locationSearchError}</p>}
          <div className="max-h-56 overflow-y-auto rounded-md border border-[#E2DFD7] bg-white">
            {locationResults.length === 0 ? (
              <p className="p-3 text-sm text-slate-500">{locationSearching ? "Searching map..." : "Search results will appear here. Select one result to locate it on the map."}</p>
            ) : locationResults.map((result) => (
              <button key={`${result.place_id}-${result.lat}-${result.lon}`} type="button" className="block w-full border-b border-[#E2DFD7] p-3 text-left text-sm last:border-b-0 hover:bg-[#F7F6F2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]" disabled={saving} onClick={() => previewLocationResult(result)}>
                <span className="block font-semibold text-[#1A2420]">{result.name || result.display_name?.split(",")[0] || "Location"}</span>
                <span className="mt-1 block text-xs text-slate-500">{result.display_name}</span>
                <span className="mt-2 inline-flex text-xs font-black text-[#2D7A65]">Locate on map</span>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-[#E2DFD7] bg-[#FFFCF7] p-2">
            <span className="min-w-0 text-xs font-semibold text-slate-500">{hasSelectedLocation ? locationForm.name : "No location selected"}</span>
            <Btn type="button" disabled={saving || !hasSelectedLocation} onClick={() => attachLocationResult()}>
              <MapPin className="h-4 w-4" /> Set location
            </Btn>
          </div>
        </div>
        <div
          className="relative min-h-72 touch-none overflow-hidden rounded-lg border border-[#E2DFD7] bg-[#F7F6F2] cursor-grab active:cursor-grabbing"
          role="application"
          aria-label="Interactive Geoapify map. Drag to move, use zoom controls, or click the map to pick a location."
          onPointerDown={handleMapPointerDown}
          onPointerMove={handleMapPointerMove}
          onPointerUp={handleMapPointerUp}
          onPointerCancel={() => { mapDragRef.current = null; }}
          onWheel={(e) => {
            e.preventDefault();
            zoomLocationMap(e.deltaY > 0 ? -1 : 1);
          }}
        >
          {tileMap ? (
            <>
              <div
                className="absolute grid"
                style={{
                  left: "50%",
                  top: "50%",
                  width: `${tileMap.tileCount * TILE_SIZE}px`,
                  height: `${tileMap.tileCount * TILE_SIZE}px`,
                  gridTemplateColumns: `repeat(${tileMap.tileCount}, ${TILE_SIZE}px)`,
                  gridTemplateRows: `repeat(${tileMap.tileCount}, ${TILE_SIZE}px)`,
                  marginLeft: `${-tileMap.centerX}px`,
                  marginTop: `${-tileMap.centerY}px`,
                }}
              >
                {tileMap.tiles.map((tile) => <img key={tile.key} alt="" src={tile.src} className="h-64 w-64" draggable="false" />)}
              </div>
              <div className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-full items-center justify-center rounded-full bg-[#2D7A65] text-white shadow-lg ring-4 ring-white">
                <MapPin className="h-5 w-5" />
              </div>
              <div className="absolute bottom-2 left-2 rounded-md bg-white/95 px-2 py-1 text-xs font-semibold text-[#17211D] shadow-sm">{locationForm.name}</div>
              <div className="absolute right-2 top-2 flex flex-col overflow-hidden rounded-md bg-white shadow-sm ring-1 ring-[#E2DFD7]" onPointerDown={(e) => e.stopPropagation()}>
                <button type="button" className="flex h-9 w-9 items-center justify-center border-b border-[#E2DFD7] text-lg font-black text-[#17211D] hover:bg-[#F7F6F2]" aria-label="Zoom in" onClick={(e) => { e.stopPropagation(); zoomLocationMap(1); }}>+</button>
                <button type="button" className="flex h-9 w-9 items-center justify-center text-lg font-black text-[#17211D] hover:bg-[#F7F6F2]" aria-label="Zoom out" onClick={(e) => { e.stopPropagation(); zoomLocationMap(-1); }}>-</button>
              </div>
              <div className="absolute bottom-2 right-2 rounded-md bg-white/95 px-2 py-1 text-xs font-semibold text-[#516259] shadow-sm">Drag map or click to pick</div>
            </>
          ) : (
            <div className="flex h-72 items-center justify-center p-4 text-center text-sm text-slate-500">
              {GEOAPIFY_API_KEY ? "Search with Geoapify, then select a result to locate it on the map." : "Geoapify API key is required to show the map."}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMoreMenu = (menuId, label, items = []) => {
    const isOpen = actionMenu?.id === menuId;
    const menu = isOpen ? createPortal(
      <div
        data-itinerary-action-menu
        className="fixed z-[1100] w-56 overflow-hidden rounded-md border border-[#E2DFD7] bg-white py-1 text-sm shadow-2xl"
        style={{
          left: `${actionMenu.x}px`,
          top: `${actionMenu.y}px`,
        }}
      >
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.label}
              type="button"
              disabled={item.disabled}
              className={`flex min-h-10 w-full items-center gap-2 px-3 text-left font-medium hover:bg-[#F7F6F2] disabled:cursor-not-allowed disabled:opacity-40 ${item.destructive ? "text-red-700" : "text-[#17211D]"}`}
              onClick={() => {
                setActionMenu(null);
                item.onClick?.();
              }}
            >
              {Icon && <Icon className="h-4 w-4 shrink-0" />}
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>,
      document.body,
    ) : null;
    return (
    <>
      <button
        type="button"
        data-itinerary-action-menu
        className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-[#E2DFD7] bg-white text-slate-600 shadow-sm hover:bg-[#F7F6F2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]"
        aria-label={label}
        aria-expanded={isOpen}
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const width = 224;
          const height = Math.min(items.length * 40 + 8, 360);
          const x = Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12));
          const hasRoomBelow = rect.bottom + height + 12 < window.innerHeight;
          const y = hasRoomBelow ? rect.bottom + 6 : Math.max(12, rect.top - height - 6);
          setActionMenu((open) => (open?.id === menuId ? null : { id: menuId, x, y }));
        }}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {menu}
    </>
    );
  };

  const renderDayEditActions = (day, dayIndex, menuId, variant = "light") => {
    if (!canEdit || !day?.id) return null;
    const dark = variant === "dark";
    const totalDays = (days || []).length;
    const shellClass = dark
      ? "flex flex-wrap gap-2"
      : "flex flex-wrap gap-2";
    const editClass = dark
      ? "inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-white/20 bg-white px-3 text-xs font-black text-[#17211D] shadow-sm hover:bg-[#FFF8EF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C]"
      : "inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-[#DCA93B] bg-white px-3 text-xs font-black text-[#17211D] shadow-sm hover:bg-[#FFF8EF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]";
    return (
      <div className={shellClass}>
        <button type="button" className={editClass} aria-label={`Edit day ${day.day_number || ""}`.trim()} title="Edit day" onClick={() => editDay(day)}>
          <Pencil className="h-4 w-4" /> Edit day
        </button>
        {renderMoreMenu(menuId, "More day actions", [
          { label: "Move day up", icon: ArrowUp, disabled: dayIndex <= 0, onClick: () => moveDay(day, -1) },
          { label: "Move day down", icon: ArrowDown, disabled: dayIndex < 0 || dayIndex === totalDays - 1, onClick: () => moveDay(day, 1) },
          { label: "Duplicate day", icon: Copy, onClick: () => duplicateDay(day) },
          { label: "Delete day", icon: Trash2, destructive: true, onClick: () => deleteDay(day.id) },
        ])}
      </div>
    );
  };

  const renderDayActivityPhotoPanel = (day, photoPanelHeight = null) => {
    const groups = (day?.activities || [])
      .map((activity) => ({ activity, photos: activity.photos || [] }))
      .filter((group) => group.photos.length > 0);
    const photoCount = groups.reduce((total, group) => total + group.photos.length, 0);
    if (!photoCount) {
      return canEdit ? (
        <aside className="rounded-lg border border-dashed border-[#D8D2C4] bg-white p-4 text-sm text-slate-500 shadow-sm">
          <p className="flex items-center gap-2 font-semibold text-[#516259]"><Camera className="h-4 w-4" /> Activity photos</p>
          <p className="mt-2">Photos added in the activity editor will appear here, tagged to the activity they belong to.</p>
        </aside>
      ) : null;
    }
    const photoPanelStyle = photoPanelHeight ? { "--photo-panel-height": `${photoPanelHeight}px` } : undefined;
    return (
      <aside
        className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-[#E2DFD7] bg-white p-3 shadow-sm xl:h-[var(--photo-panel-height)] xl:max-h-[var(--photo-panel-height)] xl:self-start"
        style={photoPanelStyle}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 text-sm font-bold text-[#17211D]"><Camera className="h-4 w-4 text-[#2D7A65]" /> Activity photos</p>
            <p className="mt-1 text-xs text-slate-500">{groups.length} tagged activit{groups.length === 1 ? "y" : "ies"}</p>
          </div>
          <span className="rounded-full bg-[#F7F1E5] px-2.5 py-1 text-xs font-bold text-[#516259] ring-1 ring-[#E2DFD7]">{photoCount} photo{photoCount === 1 ? "" : "s"}</span>
        </div>
        {saving && mediaFor?.type === "activity" && <p className="mt-2 rounded-md bg-[#E7F2ED] px-2 py-1 text-xs font-semibold text-[#2D7A65]">Uploading photos...</p>}
        <div className="mt-3 min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          {groups.map(({ activity, photos }) => {
            return (
              <section key={activity.id} className="rounded-md border border-[#E2DFD7] bg-[#FFFCF7] p-2">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="truncate text-xs font-black text-[#17211D]">{activity.title || activity.name || "Activity"}</p>
                  <span className="shrink-0 text-[11px] font-semibold text-slate-500">{photos.length}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {photos.map((photo) => (
                    <figure key={photo.id} className="group relative aspect-square overflow-hidden rounded-md border border-[#E2DFD7] bg-[#F7F6F2] shadow-sm">
                      <ParticipantMediaImage photo={photo} alt={photo.alt_text || ""} className="h-full w-full object-cover" loading="lazy" />
                      {canEdit && (
                        <div className="absolute inset-x-1 top-1 flex justify-end gap-1 opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                          <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-white/95 text-slate-700 shadow-sm hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]" aria-label="Replace photo" title="Replace photo" onClick={() => openMedia({ type: "activity", id: activity.id }, photo)}><Camera className="h-3.5 w-3.5" /></button>
                          <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-white/95 text-red-700 shadow-sm hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600" aria-label="Remove photo" title="Remove photo" onClick={() => deleteMedia(photo)}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      )}
                    </figure>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </aside>
    );
  };

  const renderDayEssentialsBand = (dayEssentials) => {
    if (!dayEssentials.length) return null;
    return (
      <div className="grid gap-3 md:grid-cols-3">
        {dayEssentials.map((item) => {
          const Icon = item.icon;
          return (
            <div key={`${item.label}-${item.value}`} className="min-h-32 rounded-lg bg-[#F7F1E5] p-3 text-sm ring-1 ring-[#EAE1D2]">
              <p className="flex items-center gap-2 text-xs font-black uppercase text-[#516259]"><Icon className="h-4 w-4 text-[#2D7A65]" /> {item.label}</p>
              <p className="mt-2 leading-6 text-[#17211D]">{item.value}</p>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDayVendorsBand = (transportItems, accommodationItems) => {
    const hasVendors = transportItems.length > 0 || accommodationItems.length > 0;
    if (!hasVendors) return null;
    return (
      <aside className="rounded-lg border border-[#E2DFD7] bg-white p-3 shadow-sm">
        <p className="text-sm font-bold text-[#17211D]">Day vendors</p>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {transportItems.length > 0 && (
            <section className="rounded-md bg-[#F7F6F2] p-3">
              <h5 className="mb-2 text-xs font-black uppercase text-[#516259]">Transportation</h5>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {transportItems.map((item) => <OperationalCard key={item.id} kind="transport" item={item} onClick={() => onGoTab?.("vendors")} />)}
              </div>
            </section>
          )}
          {accommodationItems.length > 0 && (
            <section className="rounded-md bg-[#F7F6F2] p-3">
              <h5 className="mb-2 text-xs font-black uppercase text-[#516259]">Accommodation</h5>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {accommodationItems.map((item) => <OperationalCard key={item.id} kind="accommodation" item={item} onClick={() => onGoTab?.("vendors")} />)}
              </div>
            </section>
          )}
        </div>
      </aside>
    );
  };

  const renderDaySupportPanel = (day, photoPanelHeight = null) => {
    const photoPanel = renderDayActivityPhotoPanel(day, photoPanelHeight);
    if (!photoPanel) return null;
    return (
      <div className="min-h-0 space-y-3 xl:self-start">
        {photoPanel}
      </div>
    );
  };

  const generateMissingDays = async () => {
    if (dirty && !confirmAction("Discard unsaved itinerary edits before generating missing days?")) return;
    if (!confirmAction(`Generate ${missingDayCount} missing itinerary day${missingDayCount === 1 ? "" : "s"} from this trip's duration${trip.start_date ? " and start date" : ""}?`)) return;
    setGeneratingDays(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/days/generate-missing`);
      setDirty(false);
      setEditingDay(null);
      setDayForm(dayTemplate);
      toast.success(data.created ? `Generated ${data.created} itinerary day${data.created === 1 ? "" : "s"}` : "No missing itinerary days");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setGeneratingDays(false); }
  };
  const currentExperience = null;
  const renderDayEditorForm = () => {
    if (!editingDay || !canEdit) return null;
    return modalShell({
      title: editingDay.id ? "Edit day" : "Add day",
      subtitle: editingDay.id ? `D${editingDay.day_number} ${editingDay.title || ""}` : "Create a new itinerary day",
      icon: Pencil,
      testId: "day-editor-form",
      onClose: () => { setEditingDay(null); setPackingForm(packTemplate); setDirty(false); },
      children: (
        <form onSubmit={saveDay} className="flex min-h-0 flex-1 flex-col">
          <div className="summit-form__body">
          <div className="grid gap-3 md:grid-cols-3">
            <Field label="Date" hint={hasTripDateRange ? "Within trip start and end dates" : "Set trip start and end dates first"}>
              <input
                type="date"
                className={inputCls}
                value={dayForm.date}
                min={tripStartDate || undefined}
                max={tripEndDate || undefined}
                disabled={!hasTripDateRange}
                onChange={(e) => { setDayForm({ ...dayForm, date: e.target.value }); setDirty(true); }}
              />
            </Field>
            <Field label="Title" required><AutoGrowTextarea data-testid="day-title-input" required value={dayForm.title} onKeyDown={keepSingleLine} onChange={(e) => { setDayForm({ ...dayForm, title: e.target.value }); setDirty(true); }} /></Field>
            <Field label="Meals"><AutoGrowTextarea value={dayForm.meals} onKeyDown={keepSingleLine} onChange={(e) => { setDayForm({ ...dayForm, meals: e.target.value }); setDirty(true); }} placeholder="breakfast, lunch" /></Field>
            <Field label="Summary"><AutoGrowTextarea rows={2} value={dayForm.summary} onChange={(e) => { setDayForm({ ...dayForm, summary: e.target.value }); setDirty(true); }} /></Field>
            <Field label="Distance km"><input type="number" min="0" step="0.1" className={inputCls} value={dayForm.distance_km} onChange={(e) => { setDayForm({ ...dayForm, distance_km: e.target.value }); setDirty(true); }} /></Field>
            <Field label="Elevation gain m"><input type="number" min="0" className={inputCls} value={dayForm.elevation_gain_m} onChange={(e) => { setDayForm({ ...dayForm, elevation_gain_m: e.target.value }); setDirty(true); }} /></Field>
            <Field label="Duration minutes"><input type="number" min="0" className={inputCls} value={dayForm.estimated_duration_min} onChange={(e) => { setDayForm({ ...dayForm, estimated_duration_min: e.target.value }); setDirty(true); }} /></Field>
            <Field label="Detailed description"><AutoGrowTextarea rows={4} className="min-h-24 md:col-span-2" value={dayForm.description} onChange={(e) => { setDayForm({ ...dayForm, description: e.target.value }); setDirty(true); }} /></Field>
            <Field label="Safety notes"><AutoGrowTextarea rows={4} className="min-h-24" value={dayForm.safety_notes} onChange={(e) => { setDayForm({ ...dayForm, safety_notes: e.target.value }); setDirty(true); }} /></Field>
            <Field label="Packing items" hint="Separate items with commas or new lines"><AutoGrowTextarea value={packingForm.name} onChange={(e) => { setPackingForm({ ...packingForm, name: e.target.value }); setDirty(true); }} placeholder="Rain shell, meds, headlamp" /></Field>
            <Field label="Packing details" hint="Copied to the trip-wide packing list without a day tag"><AutoGrowTextarea rows={4} className="min-h-24 md:col-span-2" value={packingForm.description} onChange={(e) => { setPackingForm({ ...packingForm, description: e.target.value }); setDirty(true); }} placeholder="Why these items matter or the specification to choose" /></Field>
          </div>
          </div>
          <SummitFormActions>
            <Btn type="button" variant="secondary" onClick={() => { setEditingDay(null); setPackingForm(packTemplate); setDirty(false); }}><X className="h-4 w-4" /> Cancel</Btn>
            <Btn disabled={saving} type="submit"><Save className="h-4 w-4" /> Save draft</Btn>
          </SummitFormActions>
        </form>
      ),
    });
  };
  const renderActivityEditorForm = () => {
    if (!activityFor || !canEdit) return null;
    const day = (days || []).find((item) => item.id === activityFor) || {};
    const savedActivity = editingActivity?.id
      ? (day.activities || []).find((activity) => activity.id === editingActivity.id) || editingActivity
      : null;
    const savedLocation = (savedActivity?.activity_locations || [])[0];
    const savedPhotoCount = (savedActivity?.photos || []).length;
    const pendingPhotoCount = pendingActivityFiles.length;
    return modalShell({
      title: editingActivity ? "Edit activity" : "Add activity",
      subtitle: day.day_number ? `D${day.day_number} ${day.title || ""}` : "Itinerary activity",
      icon: Pencil,
      testId: "activity-editor-drawer",
      onClose: () => { setActivityFor(null); setEditingActivity(null); setActivityForm({ ...activityTemplate }); setPendingActivityFiles([]); setPendingActivityLocation(null); setDirty(false); },
      children: (
        <form onSubmit={saveActivity} className="flex min-h-0 flex-1 flex-col">
          <div className="summit-form__body">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Title" required><input data-testid="activity-name-input" required autoFocus className={inputCls} value={activityForm.name} onChange={(e) => { setActivityForm({ ...activityForm, name: e.target.value, title: e.target.value }); setDirty(true); }} /></Field>
            <Field label="Activity type"><input className={inputCls} value={activityForm.activity_type || ""} onChange={(e) => { setActivityForm({ ...activityForm, activity_type: e.target.value }); setDirty(true); }} placeholder="Hike, transfer, meal, briefing" /></Field>
            <Field label="Start"><input type="time" className={inputCls} value={activityForm.start_time || ""} onChange={(e) => { setActivityForm({ ...activityForm, start_time: e.target.value }); setDirty(true); }} /></Field>
            <Field label="End"><input type="time" className={inputCls} value={activityForm.end_time || ""} onChange={(e) => { setActivityForm({ ...activityForm, end_time: e.target.value }); setDirty(true); }} /></Field>
            <Field label="Difficulty"><input className={inputCls} value={activityForm.difficulty || ""} onChange={(e) => { setActivityForm({ ...activityForm, difficulty: e.target.value }); setDirty(true); }} placeholder="Easy, moderate, hard" /></Field>
            <div className="sm:col-span-2">
              <span className="mb-1.5 block text-xs font-extrabold uppercase tracking-[0.18em] text-[#516259]">Location and photos</span>
              <div className="grid gap-2 rounded-lg border border-[#E2DFD7] bg-[#FFFCF7] p-2 sm:grid-cols-2">
                <button type="button" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[#E2DFD7] bg-white px-3 text-sm font-bold text-[#17211D] shadow-sm hover:bg-[#F7F6F2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]" onClick={() => (savedActivity?.id ? openLocation(savedActivity) : openDraftActivityLocation())}>
                  <MapPin className="h-4 w-4" /> Set location
                </button>
                <label className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-md border border-[#E2DFD7] bg-white px-3 text-sm font-bold text-[#17211D] shadow-sm hover:bg-[#F7F6F2] focus-within:ring-2 focus-within:ring-[#2D7A65]">
                  <Camera className="h-4 w-4" /> Add photos
                  <input type="file" accept="image/*" multiple className="sr-only" onChange={(e) => { setPendingActivityFiles([...(e.target.files || [])]); setDirty(true); }} />
                </label>
                <span className="min-w-0 truncate rounded-md bg-white px-3 py-2 text-xs font-semibold text-[#516259] ring-1 ring-[#E2DFD7]">
                  {activityForm.location || savedLocation?.location?.name || "Location not set"}
                </span>
                <span className="rounded-md bg-white px-3 py-2 text-xs font-semibold text-[#516259] ring-1 ring-[#E2DFD7]">
                  {savedPhotoCount + pendingPhotoCount} photo{savedPhotoCount + pendingPhotoCount === 1 ? "" : "s"}{pendingPhotoCount > 0 ? `, ${pendingPhotoCount} selected` : ""}
                </span>
              </div>
              {renderActivityLocationPicker(savedActivity)}
            </div>
            <Field label="Activity description"><textarea className={inputCls + " min-h-28"} value={activityForm.description || ""} onChange={(e) => { setActivityForm({ ...activityForm, description: e.target.value }); setDirty(true); }} placeholder="Describe what happens during this activity." /></Field>
            <Field label="Safety notes"><textarea className={inputCls + " min-h-28"} value={activityForm.safety_notes || ""} onChange={(e) => { setActivityForm({ ...activityForm, safety_notes: e.target.value }); setDirty(true); }} placeholder="Risks, medication reminders, terrain notes" /></Field>
          </div>
          </div>
          <SummitFormActions>
            <Btn type="button" variant="secondary" onClick={() => { setActivityFor(null); setEditingActivity(null); setActivityForm({ ...activityTemplate }); setPendingActivityFiles([]); setPendingActivityLocation(null); setDirty(false); }}>Cancel</Btn>
            <Btn disabled={saving} type="submit"><Save className="h-4 w-4" /> {editingActivity ? "Save activity" : "Create activity"}</Btn>
          </SummitFormActions>
        </form>
      ),
    });
  };
  const renderPlannerActivityList = (day) => {
    if (!day?.id) return null;
    const activities = day.activities || [];
    return (
      <div className="space-y-3">
        {activities.length === 0 && (
          <div className="rounded-md border border-dashed border-[#E2DFD7] bg-[#FFFCF7] px-3 py-4 text-sm text-slate-500">
            <p>No activities yet. Add the first activity to set the schedule, location, photos, and activity description for this day.</p>
            {canEdit && (
              <button type="button" className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-[#BFD5C6] bg-[#2D7A65] px-3 text-xs font-black text-white shadow-sm hover:bg-[#245F50] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]" onClick={() => openActivity(day.id)}>
                <Plus className="h-4 w-4" /> Add first activity
              </button>
            )}
          </div>
        )}
        {activities.map((activity, activityIndex) => {
          const primaryLocation = (activity.activity_locations || [])[0];
          const activityPhotos = activity.photos || [];
          const hasMultipleActivities = activities.length > 1;
          const activityLabel = activity.title || activity.name || "Activity";
          return (
            <article key={activity.id} className="rounded-lg border border-[#E2DFD7] bg-white p-3 shadow-sm transition hover:border-[#BFD5C6]" data-testid={`activity-card-${activity.id}`}>
              <div className="flex min-w-0 gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#2D7A65]/10 text-sm font-black text-[#2D7A65] ring-1 ring-[#BFD5C6]">{activityIndex + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <h5 className="break-words text-base font-black leading-snug text-[#17211D]">{activityLabel}</h5>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                        {activity.start_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{activity.start_time}{activity.end_time ? `-${activity.end_time}` : ""}</span>}
                        <span className={`flex items-center gap-1 ${primaryLocation ? "" : "text-amber-700"}`}><MapPin className="h-3 w-3" />{primaryLocation?.location?.name || activity.location || "Location not set"}</span>
                        <span className="flex items-center gap-1"><Camera className="h-3 w-3" />{activityPhotos.length} photo{activityPhotos.length === 1 ? "" : "s"}</span>
                      </div>
                    </div>
                    {canEdit && (
                      <div className="flex shrink-0 items-center gap-2 sm:justify-end" aria-label={`Activity actions for ${activityLabel}`}>
                        <button type="button" className="inline-flex min-h-9 items-center justify-center gap-2 rounded-md border border-[#DCA93B] bg-[#FFF8EF] px-3 text-xs font-black text-[#17211D] shadow-sm hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]" aria-label={`Edit ${activityLabel}`} title="Edit activity" onClick={() => openActivity(day.id, activity)}><Pencil className="h-4 w-4" /> Edit</button>
                        {renderMoreMenu(`activity-${activity.id}`, "More activity actions", [
                          { label: "Move activity up", icon: ArrowUp, disabled: !hasMultipleActivities || activityIndex === 0, onClick: () => moveActivity(day, activity, -1) },
                          { label: "Move activity down", icon: ArrowDown, disabled: !hasMultipleActivities || activityIndex === activities.length - 1, onClick: () => moveActivity(day, activity, 1) },
                          { label: "Duplicate activity", icon: Copy, onClick: () => duplicateActivity(day.id, activity) },
                          { label: "Delete activity", icon: Trash2, destructive: true, onClick: () => deleteActivity(activity.id) },
                        ])}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {(activity.description || activity.safety_notes) && (
                <div className="mt-3 border-t border-[#E2DFD7] pt-3">
                  <div className="min-w-0">
                    {(activity.description || activity.safety_notes) && (
                      <div className="space-y-1 text-sm">
                        {activity.description && <p className="text-slate-600">{activity.description}</p>}
                        {activity.safety_notes && <p className="flex items-center gap-2 text-xs text-amber-700"><ShieldAlert className="h-3.5 w-3.5" /> {activity.safety_notes}</p>}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    );
  };
  const renderExperiencePanel = () => {
    if (!currentExperience) {
      return null;
    }
    return (
      <section className={`overflow-hidden rounded-lg border bg-white shadow-sm ${themeUi.border}`} data-testid="ai-itinerary-experience">
        <div className={`relative overflow-hidden bg-gradient-to-br ${themeUi.wash}`}>
          {heroPhoto && <ParticipantMediaImage photo={heroPhoto} alt={heroPhoto.alt_text || ""} className="absolute inset-0 h-full w-full object-cover opacity-70" loading="lazy" />}
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(23,33,29,0.86),rgba(23,33,29,0.42),rgba(255,250,241,0.06))]" />
          <div className="tp-ai-shimmer absolute inset-y-0 left-0 w-1/2 opacity-30" />
          <div className="relative grid gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_minmax(22rem,0.86fr)] lg:items-end">
            <div className="min-w-0 text-white">
              <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/20"><Sparkles className="h-3.5 w-3.5" /> AI itinerary experience</p>
              <h3 className="mt-4 max-w-3xl text-3xl font-black leading-tight sm:text-4xl">{currentExperience.title}</h3>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-white/80">{currentExperience.intro}</p>
              {currentExperience.route_story && <p className="mt-4 max-w-2xl text-sm font-semibold text-[#F8E0A0]">{currentExperience.route_story}</p>}
              <div className="mt-5 flex flex-wrap gap-2 text-xs font-semibold">
                <span className="rounded-full bg-white/10 px-3 py-1 ring-1 ring-white/20">{experienceDays.length} days</span>
                <span className="rounded-full bg-white/10 px-3 py-1 ring-1 ring-white/20">{experienceActivityCount} activities</span>
                {currentExperience.mood && <span className="rounded-full bg-white/10 px-3 py-1 ring-1 ring-white/20">{currentExperience.mood}</span>}
                <span className="rounded-full bg-white/10 px-3 py-1 ring-1 ring-white/20">{themeUi.label}</span>
              </div>
            </div>
            <div className="space-y-3">
              {renderExperienceRoute()}
              {canEdit && (
                <div className="flex flex-wrap justify-end gap-2">
                  <Btn type="button" variant="secondary" onClick={() => { setExperienceDraft(experience?.experience || currentExperience); setEditingExperience(true); }}><Pencil className="h-4 w-4" /> Edit copy</Btn>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="border-b border-[#E2DFD7] bg-[#FFFCF7] px-5 py-3">
          {(experience?.source || currentExperience.theme) && <p className="text-xs text-slate-500">{[currentExperience.theme, experience?.source].filter(Boolean).join(" - ")}</p>}
          {(currentExperience.warnings || []).length > 0 && (
            <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              {(currentExperience.warnings || []).map((warning, index) => <p key={`${warning}-${index}`}>{warning}</p>)}
            </div>
          )}
        </div>
        <div className="space-y-4 p-4">
          {(currentExperience.days || []).length === 0 ? (
            <p className="text-sm text-slate-500">No itinerary days are available for this experience yet.</p>
          ) : (currentExperience.days || []).map((day, index) => {
            const photos = getExperiencePhotos(day.photo_ids, 5);
            const essentials = buildDayEssentials(day);
            const attentionItems = uniqueText([
              ...(day.missing_info || []),
              ...(day.logistics || []).filter((item) => String(item || "").toLowerCase().includes("not linked")),
            ]);
            const highlights = uniqueText(day.highlights || []);
            const sourceDay = sourceForExperienceDay(day);
            const sourceDayIndex = (days || []).findIndex((item) => item.id === sourceDay.id);
            const canEditSourceDay = canEdit && sourceDay?.id;
            const chapterKey = `chapter-${sourceDay?.id || day.day_id || day.day_number || index}`;
            const sourceMeasureKey = `experience-${sourceDay?.id || day.day_id || day.day_number || index}`;
            const isChapterOpen = !!expanded[chapterKey];
            return (
              <article key={day.day_id || index} className={`tp-ai-day-card rounded-lg border bg-white shadow-sm ${isChapterOpen ? "border-[#BFD5C6]" : "border-[#D6E4D8]"}`} style={{ animationDelay: `${Math.min(index * 80, 560)}ms` }}>
                <div className={`rounded-t-lg border-b px-4 py-3 text-white ${isChapterOpen ? "border-[#49675B] bg-[#203A31]" : "border-[#D8E5DA] bg-[#25463A]"}`}>
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C] focus-visible:ring-offset-2 focus-visible:ring-offset-[#17211D]" aria-expanded={isChapterOpen} onClick={() => setExpanded((prev) => ({ ...prev, [chapterKey]: !isChapterOpen }))}>
                      {isChapterOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-white/70" /> : <ChevronRight className="h-4 w-4 shrink-0 text-white/70" />}
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#E5B85C] text-sm font-black text-[#17211D] shadow-sm">D{day.day_number}</span>
                      <div className="min-w-0">
                        <p className="text-xs font-bold uppercase text-white/60">{sourceDay?.date || day.date || "Date not set"}</p>
                        <h4 className="truncate text-xl font-black">{day.headline || `Day ${day.day_number}`}</h4>
                      </div>
                    </button>
                    <div className="flex flex-wrap items-center justify-end gap-2 text-xs font-semibold">
                      {day.movement && <span className="rounded-full bg-white/10 px-3 py-1 ring-1 ring-white/20">{day.movement}</span>}
                      {canEditSourceDay && renderDayEditActions(sourceDay, sourceDayIndex, `day-${sourceDay.id}`, "dark")}
                    </div>
                  </div>
                </div>

                {isChapterOpen && <div className="p-4">
                    <div className="space-y-5">
                      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(18rem,0.78fr)]">
                        <div className="space-y-4" ref={setDayContentMeasureRef(sourceMeasureKey)}>
                          {day.story && <p className="max-w-3xl text-base leading-7 text-slate-700">{day.story}</p>}
                          {highlights.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {highlights.slice(0, 5).map((highlight, i) => (
                                <span key={`${highlight}-${i}`} className="rounded-full bg-[#E5B85C]/20 px-3 py-1 text-xs font-bold text-[#6D4B13]">{highlight}</span>
                              ))}
                            </div>
                          )}
                          {canEditSourceDay ? (
                            <div className="rounded-lg border border-[#E2DFD7] bg-[#FFFCF7] p-3">
                              <div className="mb-3 flex items-center justify-between gap-2">
                                <p className="flex items-center gap-2 text-sm font-bold text-[#17211D]"><Flag className="h-4 w-4 text-[#2D7A65]" /> Today's flow</p>
                                <div className="flex flex-wrap items-center justify-end gap-2">
                                  <span className="text-xs font-semibold text-slate-500">{(sourceDay.activities || []).length} planned stop{(sourceDay.activities || []).length === 1 ? "" : "s"}</span>
                                  <button type="button" className="inline-flex min-h-8 items-center justify-center gap-1.5 rounded-md border border-[#BFD5C6] bg-[#2D7A65] px-2.5 text-xs font-black text-white shadow-sm hover:bg-[#245F50] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]" onClick={() => openActivity(sourceDay.id)}>
                                    <Plus className="h-3.5 w-3.5" /> Add activity
                                  </button>
                                </div>
                              </div>
                              {renderPlannerActivityList(sourceDay)}
                            </div>
                          ) : day.activity_refs?.length > 0 ? (
                            <div className="rounded-lg border border-[#E2DFD7] bg-[#FFFCF7] p-3">
                              <div className="mb-3 flex items-center justify-between gap-2">
                                <p className="flex items-center gap-2 text-sm font-bold text-[#17211D]"><Flag className="h-4 w-4 text-[#2D7A65]" /> Today's flow</p>
                                <span className="text-xs font-semibold text-slate-500">{day.activity_refs.length} planned stop{day.activity_refs.length === 1 ? "" : "s"}</span>
                              </div>
                              <div className="space-y-2">
                                {day.activity_refs.map((activity, activityIndex) => (
                                  <div key={activity.activity_id} className="grid gap-3 rounded-md border border-[#E2DFD7] bg-white p-3 text-sm sm:grid-cols-[2rem_minmax(0,1fr)]">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2D7A65]/10 text-xs font-black text-[#2D7A65]">{activityIndex + 1}</span>
                                    <div className="min-w-0">
                                      <p className="truncate font-bold text-[#17211D]">{activity.title}</p>
                                      <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                                        {activity.time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{activity.time}</span>}
                                        {activity.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{activity.location}</span>}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="rounded-md border border-dashed border-[#E2DFD7] bg-[#FFFCF7] p-4 text-sm text-slate-500">Activities are not set for this day yet.</div>
                          )}
                        </div>
                        {canEditSourceDay ? renderDayActivityPhotoPanel(sourceDay, photoPanelHeights[sourceMeasureKey]) : renderDayPhotoStory(photos)}
                      </div>

                      {essentials.length > 0 && (
                        <div className="rounded-lg border border-[#E2DFD7] bg-[#F7F6F2] p-3">
                          <p className="mb-3 text-xs font-black uppercase text-[#516259]">Included today</p>
                          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                            {essentials.map((item) => {
                              const Icon = item.icon;
                              return (
                                <div key={`${item.label}-${item.value}`} className="rounded-md bg-white p-3 text-sm shadow-sm">
                                  <p className="flex items-center gap-2 text-xs font-black uppercase text-[#516259]"><Icon className="h-4 w-4 text-[#2D7A65]" /> {item.label}</p>
                                  <p className="mt-2 leading-6 text-[#17211D]">{item.value}</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {!canEditSourceDay && photos.length > 3 && renderExperiencePhotos((day.photo_ids || []).slice(3))}
                      {attentionItems.length > 0 && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                          <p className="font-bold">Needs planner attention</p>
                          <p className="mt-1">{attentionItems.join(" - ")}</p>
                        </div>
                      )}
                    </div>
                </div>}
              </article>
            );
          })}
        </div>
      </section>
    );
  };

  if (error) return <ErrorState message={error} onRetry={safeReload} />;
  if (days === null) return <LoadingState label="Loading itinerary..." />;

  return (
    <div className="space-y-4" data-testid="integrated-itinerary-editor">
      <input ref={mediaFileInputRef} type="file" accept="image/*" multiple className="sr-only" onChange={handlePhotoFileSelect} />
      {renderDayEditorForm()}
      {renderActivityEditorForm()}
      <section className="space-y-4 rounded-2xl border border-[#E2DFD7] bg-white/65 p-4 shadow-[0_10px_28px_rgba(37,49,43,0.05)]">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#718078]">Itinerary builder</p>
            <p className="mt-1 text-sm font-medium text-[#1A2420]">{days.length} of {trip.duration_days} days planned</p>
            {dirty && <p className="mt-1 text-xs text-amber-700">Unsaved changes in the open form.</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            {canEdit && missingDayCount > 0 && (
              <Btn
                variant="secondary"
                data-testid="generate-missing-days-button"
                disabled={generatingDays}
                onClick={generateMissingDays}
              >
                <Plus className="h-4 w-4" /> {generatingDays ? "Generating..." : "Generate remaining days"}
              </Btn>
            )}
            {canEdit && <Btn data-testid="add-day-button" onClick={newDay}><Plus className="h-4 w-4" /> Add day</Btn>}
          </div>
        </div>

        {renderExperiencePanel()}

        {days.length === 0 ? (
          <EmptyState title="No days planned" description="Build the itinerary as expandable days with activities, locations, photos, packing, safety notes, and route previews." />
        ) : days.map((day, dayIndex) => {
        const isOpen = expanded[day.id];
        const dayPacking = dayPackingItems(day);
        const activities = day.activities || [];
        const vendorAssignments = day.vendor_assignments || [];
        const vendorTransportItems = vendorAssignments
          .filter((assignment) => assignment.vendor?.category === "transport")
          .map((assignment) => vendorAssignmentLogisticsItem(assignment, "transport"));
        const vendorAccommodationItems = vendorAssignments
          .filter((assignment) => assignment.vendor?.category === "accommodation")
          .map((assignment) => vendorAssignmentLogisticsItem(assignment, "accommodation"));
        const transportItems = [...(day.transportation || []), ...vendorTransportItems];
        const accommodationItems = [...(day.accommodations || []), ...vendorAccommodationItems];
        const dayMetrics = [
          day.distance_km != null ? `${day.distance_km} km` : null,
          day.elevation_gain_m != null ? `${day.elevation_gain_m} m gain` : null,
          day.estimated_duration_min != null ? `${Math.round(day.estimated_duration_min / 60)} hr` : null,
          `${activities.length} planned stop${activities.length === 1 ? "" : "s"}`,
        ].filter(Boolean);
        const dayEssentials = [
          (day.meals || []).length ? { label: "Meals", icon: Utensils, value: joinText(day.meals) } : null,
          dayPacking.length ? { label: "Pack", icon: Backpack, value: joinText(dayPacking.map((item) => item.name)) } : null,
          day.safety_notes ? { label: "Care", icon: ShieldAlert, value: day.safety_notes } : null,
        ].filter(Boolean);
        const attentionItems = uniqueText([
          activities.length === 0 ? "No activities set for this day." : null,
          activities.some((activity) => !(activity.activity_locations || []).length && !activity.location) ? "Activity location missing." : null,
          transportItems.length === 0 ? "Transport not linked." : null,
          accommodationItems.length === 0 ? "Accommodation not linked." : null,
          !day.safety_notes ? "Safety notes not set." : null,
        ]);
        return (
          <section key={day.id} className={`overflow-hidden rounded-lg border bg-[#FFFAF1] shadow-sm transition ${isOpen ? "border-[#BFD5C6]" : "border-[#E2DFD7]"}`} data-testid={`itinerary-day-${day.day_number}`}>
            <header className="p-4 sm:p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <button type="button" onClick={() => setExpanded({ ...expanded, [day.id]: !isOpen })} className="flex min-h-12 min-w-0 flex-1 items-start gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65] focus-visible:ring-offset-2" aria-expanded={isOpen}>
                  {isOpen ? <ChevronDown className="mt-4 h-4 w-4 shrink-0 text-[#516259]" /> : <ChevronRight className="mt-4 h-4 w-4 shrink-0 text-[#516259]" />}
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#E5B85C] text-sm font-black text-[#17211D] shadow-sm">D{day.day_number}</span>
                  <span className="min-w-0">
                    <span className="block text-[11px] font-black uppercase text-[#516259]">{day.date || "Date not set"}</span>
                    <span className="mt-0.5 block truncate text-xl font-black text-[#17211D]">{day.title || `Day ${day.day_number}`}</span>
                    {(day.summary || day.description) && <span className="mt-1 block line-clamp-2 text-xs font-semibold text-[#2D7A65]">{day.summary || day.description}</span>}
                  </span>
                </button>
                <div className="flex flex-wrap items-center gap-2 md:justify-end">
                  {dayMetrics.slice(0, 4).map((item) => (
                    <span key={item} className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 ring-1 ring-[#E2DFD7]">{item}</span>
                  ))}
                  {canEdit && renderDayEditActions(day, dayIndex, `fallback-day-${day.id}`)}
                </div>
              </div>
            </header>
            {isOpen && (
              <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(16rem,0.55fr)]">
                  <div className="space-y-5" ref={setDayContentMeasureRef(day.id)}>
                    {(day.summary || day.description) && (
                      <div className="border-t border-[#E2DFD7] pt-4 text-sm leading-6 text-slate-700">
                        {day.summary && <p className="font-semibold text-[#17211D]">{day.summary}</p>}
                        {day.description && <p className={day.summary ? "mt-2" : ""}>{day.description}</p>}
                      </div>
                    )}

                    <div className="rounded-lg border border-[#E2DFD7] bg-white p-3 shadow-sm">
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <p className="flex items-center gap-2 text-sm font-bold text-[#17211D]"><Flag className="h-4 w-4 text-[#2D7A65]" /> Today's flow</p>
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <span className="text-xs font-semibold text-slate-500">{activities.length} planned stop{activities.length === 1 ? "" : "s"}</span>
                          {canEdit && (
                            <button type="button" className="inline-flex min-h-8 items-center justify-center gap-1.5 rounded-md border border-[#BFD5C6] bg-[#2D7A65] px-2.5 text-xs font-black text-white shadow-sm hover:bg-[#245F50] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]" onClick={() => openActivity(day.id)}>
                              <Plus className="h-3.5 w-3.5" /> Add activity
                            </button>
                          )}
                        </div>
                      </div>
                      {renderPlannerActivityList(day)}
                    </div>
                  </div>
                  {renderDaySupportPanel(day, photoPanelHeights[day.id])}
                </div>
                {dayEssentials.length > 0 && <div className="mt-4">{renderDayEssentialsBand(dayEssentials)}</div>}
                {(transportItems.length > 0 || accommodationItems.length > 0) && <div className="mt-4">{renderDayVendorsBand(transportItems, accommodationItems)}</div>}
                {attentionItems.length > 0 && (
                  <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                    <p className="font-bold">Needs planner attention</p>
                    <p className="mt-1">{attentionItems.join(" - ")}</p>
                  </div>
                )}
              </div>
            )}
          </section>
        );
        })}
      </section>

    </div>
  );
}
